import { t } from '@superset-ui/core';

/**
 * Parses a multi-language text string into an object with language codes as keys.
 * 
 * @param input - The input string in the format "[lang] text [/lang]"
 * @returns An object with language codes as keys and corresponding texts as values
 */
export function parseMultiLangText(input: string): Record<string, string> {
  const langRegex = /\[([a-z]{2}(?:-[a-zA-Z]{2})?)\]([\s\S]*?)\[\/\1\]/gi;
  const result: Record<string, string> = {};
  let match: RegExpExecArray | null;

  while ((match = langRegex.exec(input)) !== null) {
    const [, lang, text] = match;
    result[lang.toLowerCase()] = text.trim();
  }

  // If no languages were found, treat the entire input as English
  if (Object.keys(result).length === 0) {
    result['en'] = input.trim();
  }

  return result;
}

/**
 * Retrieves the text for a specific language from a parsed multi-language object.
 * Falls back to English if the requested language is not available.
 * 
 * @param translations - The parsed multi-language object
 * @param lang - The desired language code
 * @returns The text in the requested language, or English as fallback
 */
export function getTextForLanguage(translations: Record<string, string>, lang: string): string {
  // Normalize the requested language code to lowercase
  const normalizedLang = lang.toLowerCase();
  
  // Try exact match first
  if (translations[normalizedLang]) {
    return translations[normalizedLang];
  }

  // Try matching just the language part (e.g., 'es' for 'es-MX')
  const baseLang = normalizedLang.split('-')[0];
  const matchingKey = Object.keys(translations).find(key => 
    key.toLowerCase() === normalizedLang || key.toLowerCase().startsWith(baseLang + '-')
  );

  if (matchingKey) {
    return translations[matchingKey];
  }

  // Fallback to English or first available translation
  return translations['en'] || Object.values(translations)[0] || '';
}

/**
 * Retrieves the language code from the URL parameters.
 * Defaults to 'en' if no language parameter is found.
 * 
 * @returns The language code as a string
 */
export function getLangFromUrlParams(): string {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('lang') || 'en';
}

/**
 * Parses and localizes multi-language text based on the current language.
 * Supports interpolation of values.
 * 
 * @param text - The multi-language text to localize
 * @param currentLang - The current language code
 * @param interpolations - An object containing key-value pairs for interpolation
 * @returns The localized text for the current language with interpolated values
 */
export function localizeText(text: string, currentLang: string, interpolations?: Record<string, string | number>): string {
  if (typeof text !== 'string') {
    // console.error('Expected a string for text, but received:', text);
    return ''; // Return an empty string or a default value
  }

  const parsed = parseMultiLangText(text);
  let localizedText = getTextForLanguage(parsed, currentLang);

  if (interpolations) {
    Object.entries(interpolations).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      localizedText = localizedText.replace(regex, String(value));
    });
  }

  return localizedText;
}

export const getUrlParams = (key?: string): Record<string, string> | string | null => {
  const currentUrl = window.location.href;
  const urlParams = new URLSearchParams(new URL(currentUrl).search);

  if (key) {
    return urlParams.get(key); // Returns the value for the specific key or null if not found
  }

  const paramsObject: Record<string, string> = {};
  urlParams.forEach((value, key) => {
    paramsObject[key] = value;
  });

  return paramsObject; // Returns all parameters as an object
};

export const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Score conversion mapping tables and functions

// IELTS-EPS mapping
export const IELTS_EPS_MAPPING = {
  1.5: [0, 27], 2: [27, 33], 2.5: [33, 39], 3: [39, 45], 3.5: [45, 50], 4: [50, 54],
  4.5: [54, 59], 5: [59, 63], 5.5: [63, 67], 6: [67, 72], 6.5: [72, 77], 7: [77, 80],
  7.5: [80, 83], 8: [83, 87], 8.5: [87, 91], 9: [91, 100]
};

// IELTS-CEFR mapping
export const IELTS_CEFR_MAPPING = {
  1.5: 'A1', 2: 'A1', 2.5: 'A1', 3: 'A2', 3.5: 'A2', 4: 'B1', 4.5: 'B1',
  5: 'B1', 5.5: 'B2', 6: 'B2', 6.5: 'B2', 7: 'C1', 7.5: 'C1', 8: 'C1',
  8.5: 'C2', 9: 'C2'
};

// CEFR-TOEIC mapping from
export const CEFR_TOEIC_MAPPING = {
  'A1': [0, 90], 'A2': [90, 120], 'B1': [120, 160], 'B2': [160, 180], 'C1': [180, 200], 'C2': [200, 200]
};

// Get CEFR-EPS mapping by finding min/max EPS for each CEFR level
const getCefrEpsMapping = () => {
  const cefrLevels = [...new Set(Object.values(IELTS_CEFR_MAPPING))];
  const cefrEps: Record<string, [number, number]> = {};
  
  cefrLevels.forEach(cefr => {
    cefrEps[cefr] = [100, 0]; // [min, max]
  });
  
  Object.entries(IELTS_CEFR_MAPPING).forEach(([ielts, cefr]) => {
    const ieltsFloat = parseFloat(ielts);
    const [minEps, maxEps] = IELTS_EPS_MAPPING[ieltsFloat as keyof typeof IELTS_EPS_MAPPING];
    cefrEps[cefr][0] = Math.min(cefrEps[cefr][0], minEps);
    cefrEps[cefr][1] = Math.max(cefrEps[cefr][1], maxEps);
  });
  
  return cefrEps;
};

export const CEFR_EPS_MAPPING = getCefrEpsMapping();

// Score mapping table based on EPS percentage (for GEPT and EIKEN)
export const SCORE_MAPPING_TABLE = [
  { level: 'Beginner', eps: [0, 39], gept: 'N/A', eiken: 'Grade 3, 4, 5' },
  { level: 'Elementary', eps: [39, 50], gept: 'Elementary', eiken: 'Grade Pre-2' },
  { level: 'Intermediate', eps: [50, 63], gept: 'Intermediate', eiken: 'Grade 2' },
  { level: 'Upper Intermediate', eps: [63, 77], gept: 'High Intermediate', eiken: 'Grade Pre-1' },
  { level: 'Advanced', eps: [77, 87], gept: 'Advanced', eiken: 'Grade 1' },
  { level: 'Mastery', eps: [87, 100], gept: 'Advanced', eiken: 'Grade 1' }
];

// Linear mapping function
const linearMapping = (score: number, inputRange: [number, number], outputRange: [number, number]): number => {
  if (score <= inputRange[0]) return outputRange[0];
  if (score >= inputRange[1]) return outputRange[1];
  
  // Linear interpolation
  const ratio = (score - inputRange[0]) / (inputRange[1] - inputRange[0]);
  return outputRange[0] + ratio * (outputRange[1] - outputRange[0]);
};

/**
 * Convert EPS to IELTS
 * @param eps - The EPS score (0-100)
 * @returns The IELTS score or null if invalid
 */
export const computeIelts = (eps: number): number | null => {
  if (!eps || eps < 0 || eps > 100) return null;
  
  const roundedEps = Math.round(eps);
  for (const [ielts, [minEps, maxEps]] of Object.entries(IELTS_EPS_MAPPING)) {
    if (roundedEps >= minEps && roundedEps < maxEps) {
      return parseFloat(ielts);
    }
  }
  return null;
};

/**
 * Convert IELTS to CEFR
 * @param ielts - The IELTS score
 * @returns The CEFR level or null if invalid
 */
export const computeCefr = (ielts: number): string | null => {
  if (!ielts) return null;
  return IELTS_CEFR_MAPPING[ielts as keyof typeof IELTS_CEFR_MAPPING] || null;
};

/**
 * Convert CEFR + EPS to TOEIC
 * @param cefr - The CEFR level
 * @param eps - The EPS score
 * @returns The TOEIC score or null if invalid
 */
export const computeToeic = (cefr: string, eps: number): number | null => {
  if (!cefr || !eps) return null;
  
  const cefrEpsRange = CEFR_EPS_MAPPING[cefr];
  const cefrToeicRange = CEFR_TOEIC_MAPPING[cefr as keyof typeof CEFR_TOEIC_MAPPING];
  
  if (!cefrEpsRange || !cefrToeicRange) return null;
  
  const toeicScore = linearMapping(eps, cefrEpsRange as [number, number], cefrToeicRange as [number, number]);
  return Math.floor(toeicScore / 10) * 10; // Round down to nearest 10
};

/**
 * Function to convert EPS score to custom score values
 * @param epsScore - The EPS score (0-100)
 * @returns Object containing all converted scores, or empty object if invalid input
 */
export const convertEpsToCustomScores = (epsScore: number): Record<string, string> => {
  // Handle undefined, null, NaN, and non-numeric values
  if (epsScore === undefined || epsScore === null || Number.isNaN(epsScore) || typeof epsScore !== 'number') {
    return {};
  }
  
  // Handle negative numbers, zero, and out-of-range values
  if (epsScore <= 0 || epsScore > 100 || !Number.isFinite(epsScore)) {
    return {};
  }
  
  try {
    // Convert EPS to IELTS first
    const ielts = computeIelts(epsScore);
    if (!ielts || Number.isNaN(ielts)) return {};
    
    // Convert IELTS to CEFR
    const cefr = computeCefr(ielts);
    
    // Convert CEFR + EPS to TOEIC
    const toeic = cefr ? computeToeic(cefr, epsScore) : null;
    
    // Format IELTS score for display (whole numbers don't need decimal, half scores keep .5)
    const ieltsFormatted = ielts % 1 === 0 ? ielts.toFixed(0) : ielts.toString();
    
    // Get GEPT and EIKEN from the existing table mapping
    const matchingLevel = SCORE_MAPPING_TABLE.find(level => 
      epsScore >= level.eps[0] && epsScore < level.eps[1]
    );

    // Use t() for translation of values
    const result: Record<string, string> = {
      'CEFR': cefr ? t(cefr) : '',
      'IELTS': ieltsFormatted,
    };

    if (toeic !== null && Number.isFinite(toeic) && !Number.isNaN(toeic)) {
      result['TOEIC'] = toeic.toString();
    }

    if (matchingLevel) {
      result['GEPT'] = t(matchingLevel.gept);
      result['EIKEN'] = t(matchingLevel.eiken);
      result['level'] = t(matchingLevel.level);
    }
    
    return result;
  } catch (error) {
    // Handle any unexpected errors during conversion
    console.error('Error converting EPS to custom scores:', error);
    return {};
  }
};

